import { useEffect, useState } from "react";
import { Users, UserPlus, Mail, MessageSquare, Smartphone, Loader2, Trash2, Send, CheckCircle2, ChevronDown, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

/**
 * Feb-2026 — Clubs / Vereine: Mitglieder erfassen und Vermittler-Transaktionscode
 * per E-Mail / WhatsApp / SMS verteilen. Sichtbar nur für Service-Provider
 * der Branche `clubs_vereine`.
 */
export default function ClubMembersCard() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({ total: 0, code_sent: 0, active: 0 });
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [sendingId, setSendingId] = useState(null);
    const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
    const [bulkRunning, setBulkRunning] = useState(false);
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", note: "" });
    const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const load = async () => {
        try {
            const r = await api.get("/service-providers/me/club/members");
            setItems(r.data.items || []);
            setCounts(r.data.counts || {});
            setCode(r.data.transaction_code || "");
        } catch (e) {
            // 403 → Branche ≠ clubs_vereine → Komponente versteckt sich oben
            if (e.response?.status !== 403) {
                toast.error(e.response?.data?.detail || "Mitglieder konnten nicht geladen werden");
            }
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!form.first_name.trim() && !form.last_name.trim()) { toast.error("Name erforderlich"); return; }
        if (!form.email.trim() && !form.phone.trim()) { toast.error("E-Mail oder Telefon erforderlich"); return; }
        setSubmitting(true);
        try {
            await api.post("/service-providers/me/club/members", form);
            toast.success("Mitglied erfasst");
            setForm({ first_name: "", last_name: "", email: "", phone: "", note: "" });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erfassung fehlgeschlagen");
        } finally {
            setSubmitting(false);
        }
    };

    const sendCode = async (memberId, channels) => {
        setSendingId(memberId + ":" + channels.join(","));
        try {
            const r = await api.post(`/service-providers/me/club/members/${memberId}/send-code`, { channels });
            const labels = { email: "E-Mail", whatsapp: "WhatsApp", sms: "SMS" };
            const sent = (r.data.channels || []).map((c) => labels[c]).join(" · ");
            toast.success(`Code gesendet via ${sent}`, { description: r.data.mocked ? "MOCKED — Live-Versand benötigt Provider-Keys (Resend / Twilio)" : undefined });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
        } finally {
            setSendingId(null);
        }
    };

    const remove = async (memberId) => {
        if (!window.confirm("Mitglied wirklich entfernen?")) return;
        try {
            await api.delete(`/service-providers/me/club/members/${memberId}`);
            toast.success("Mitglied entfernt");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen");
        }
    };

    const bulkSend = async (channels) => {
        const label = channels.length === 3 ? "alle Kanäle" : channels.map((c) => ({email:"E-Mail",whatsapp:"WhatsApp",sms:"SMS"}[c])).join(" · ");
        if (!window.confirm(`Code an alle ${counts.total} Mitglieder via ${label} senden?`)) return;
        setBulkMenuOpen(false);
        setBulkRunning(true);
        try {
            const r = await api.post("/service-providers/me/club/members/bulk-send-code", { channels });
            const s = r.data.summary || {};
            toast.success(`Bulk-Versand fertig: ${s.sent} versendet, ${s.skipped} übersprungen`, {
                description: r.data.mocked ? "MOCKED — Live-Versand benötigt Provider-Keys (Resend / Twilio)" : undefined,
            });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bulk-Versand fehlgeschlagen");
        } finally {
            setBulkRunning(false);
        }
    };

    if (loading) return null;

    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="club-members-card">
            <button
                type="button"
                onClick={() => setFormOpen((o) => !o)}
                className="w-full flex items-center gap-3 p-5 text-left hover:bg-gray-50 transition-colors"
                aria-expanded={formOpen}
                data-testid="club-members-toggle"
            >
                <Users className="w-5 h-5 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <h3 className="font-display font-bold text-base">Club-Mitglieder & Transaktionscode</h3>
                    <p className="text-xs text-gray-600 mt-0.5">
                        {counts.total} erfasst · {counts.code_sent} Code versendet — Klicken zum Erfassen
                    </p>
                </div>
                {code && (
                    <span className="text-[10px] uppercase tracking-wider font-bold bg-[#111827] text-[#FFCC00] px-2 py-0.5 rounded-sm font-mono-num shrink-0" title="Vermittler-Transaktionscode dieses Clubs">
                        {code}
                    </span>
                )}
                <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform shrink-0 ${formOpen ? "rotate-180" : ""}`} />
            </button>

            {formOpen && (
                <div className="px-6 pb-6 border-t border-gray-100" data-testid="club-members-form">
                    <p className="text-xs text-gray-600 mt-3 mb-3">
                        Erfassen Sie ein neues Mitglied. Nach dem Anlegen können Sie den Vermittler-Transaktionscode <strong className="font-mono-num">{code || "—"}</strong> per E-Mail, WhatsApp oder SMS direkt aus der Liste verschicken.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[11px] uppercase tracking-wider font-bold">Vorname</Label>
                            <Input value={form.first_name} onChange={(e) => upd("first_name", e.target.value)} placeholder="z. B. Anna" className="rounded-sm" data-testid="club-member-first" />
                        </div>
                        <div>
                            <Label className="text-[11px] uppercase tracking-wider font-bold">Nachname</Label>
                            <Input value={form.last_name} onChange={(e) => upd("last_name", e.target.value)} placeholder="z. B. Becker" className="rounded-sm" data-testid="club-member-last" />
                        </div>
                        <div>
                            <Label className="text-[11px] uppercase tracking-wider font-bold">E-Mail</Label>
                            <Input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} placeholder="anna@example.com" className="rounded-sm" data-testid="club-member-email" />
                        </div>
                        <div>
                            <Label className="text-[11px] uppercase tracking-wider font-bold">Handynummer</Label>
                            <Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="+49 170 1234567" inputMode="tel" className="rounded-sm" data-testid="club-member-phone" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-[11px] uppercase tracking-wider font-bold">Notiz (optional)</Label>
                            <Textarea value={form.note} onChange={(e) => upd("note", e.target.value)} rows={2} placeholder="z. B. Mitglied seit 2018, Schatzmeister" className="rounded-sm text-sm" data-testid="club-member-note" />
                        </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                        <Button onClick={submit} disabled={submitting} className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider" data-testid="club-member-submit">
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1.5" />Mitglied erfassen</>}
                        </Button>
                    </div>
                </div>
            )}

            {/* Liste — fortlaufend, immer sichtbar */}
            <div className="px-6 pb-6 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">
                        Erfasste Mitglieder ({counts.total})
                    </div>
                    <div className="flex items-center gap-2">
                        {counts.total > 0 && (
                            <div className="text-[10px] text-gray-500">
                                <span className="font-mono-num">{counts.code_sent}</span> haben den Code erhalten
                            </div>
                        )}
                        {counts.total > 0 && (
                            <div className="relative">
                                <Button
                                    type="button"
                                    size="sm"
                                    onClick={() => setBulkMenuOpen((o) => !o)}
                                    disabled={bulkRunning}
                                    className="bg-[#111827] hover:bg-black text-[#FFCC00] font-bold rounded-sm uppercase tracking-wider text-xs h-8"
                                    data-testid="club-bulk-send-btn"
                                    title={`An alle ${counts.total} Mitglieder senden`}
                                >
                                    {bulkRunning
                                        ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                                        : <Zap className="w-3.5 h-3.5 mr-1" />}
                                    An alle {counts.total} senden
                                    <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${bulkMenuOpen ? "rotate-180" : ""}`} />
                                </Button>
                                {bulkMenuOpen && (
                                    <div className="absolute right-0 top-full mt-1 z-30 bg-white border-2 border-[#FFCC00] rounded-sm shadow-xl py-1 min-w-[220px]" data-testid="club-bulk-menu">
                                        <button type="button" onClick={() => bulkSend(["email"])} className="w-full px-3 py-2 text-left text-xs font-bold uppercase tracking-wider hover:bg-[#FFCC00]/20 inline-flex items-center gap-2" data-testid="club-bulk-email">
                                            <Mail className="w-3.5 h-3.5" /> nur E-Mail
                                        </button>
                                        <button type="button" onClick={() => bulkSend(["whatsapp"])} className="w-full px-3 py-2 text-left text-xs font-bold uppercase tracking-wider hover:bg-[#FFCC00]/20 inline-flex items-center gap-2" data-testid="club-bulk-whatsapp">
                                            <MessageSquare className="w-3.5 h-3.5" /> nur WhatsApp
                                        </button>
                                        <button type="button" onClick={() => bulkSend(["sms"])} className="w-full px-3 py-2 text-left text-xs font-bold uppercase tracking-wider hover:bg-[#FFCC00]/20 inline-flex items-center gap-2" data-testid="club-bulk-sms">
                                            <Smartphone className="w-3.5 h-3.5" /> nur SMS
                                        </button>
                                        <div className="border-t border-gray-200 my-1" />
                                        <button type="button" onClick={() => bulkSend(["email","whatsapp","sms"])} className="w-full px-3 py-2 text-left text-xs font-bold uppercase tracking-wider bg-[#FFCC00]/20 hover:bg-[#FFCC00]/40 inline-flex items-center gap-2" data-testid="club-bulk-all">
                                            <Send className="w-3.5 h-3.5" /> alle 3 Kanäle
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {items.length === 0 ? (
                    <div className="text-xs text-gray-500" data-testid="club-members-empty">Noch keine Mitglieder erfasst.</div>
                ) : (
                    <ul className="divide-y divide-gray-100" data-testid="club-members-list">
                        {items.map((m) => {
                            const fullName = [m.first_name, m.last_name].filter(Boolean).join(" ") || "—";
                            const sentSet = new Set(m.code_sent_channels || []);
                            return (
                                <li key={m.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`club-member-${m.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-sm truncate">{fullName}</div>
                                        <div className="text-[11px] text-gray-500 flex flex-wrap items-center gap-x-2 truncate">
                                            {m.email && <span className="inline-flex items-center gap-0.5"><Mail className="w-3 h-3" />{m.email}</span>}
                                            {m.phone && <span className="inline-flex items-center gap-0.5"><Smartphone className="w-3 h-3" />{m.phone}</span>}
                                        </div>
                                        {m.note && <div className="text-[10px] text-gray-400 truncate mt-0.5">{m.note}</div>}
                                        {m.code_sent_at && (
                                            <div className="text-[10px] text-emerald-700 mt-0.5 inline-flex items-center gap-1 font-bold">
                                                <CheckCircle2 className="w-3 h-3" /> Code versendet · {Array.from(sentSet).join(", ")}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        {m.email && (
                                            <Button size="sm" variant="outline" disabled={sendingId === m.id + ":email"} onClick={() => sendCode(m.id, ["email"])} className="rounded-sm h-8 px-2" title="Code per E-Mail senden" data-testid={`club-send-email-${m.id}`}>
                                                {sendingId === m.id + ":email" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                                            </Button>
                                        )}
                                        {m.phone && (
                                            <Button size="sm" variant="outline" disabled={sendingId === m.id + ":whatsapp"} onClick={() => sendCode(m.id, ["whatsapp"])} className="rounded-sm h-8 px-2" title="Code per WhatsApp senden" data-testid={`club-send-whatsapp-${m.id}`}>
                                                {sendingId === m.id + ":whatsapp" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
                                            </Button>
                                        )}
                                        {m.phone && (
                                            <Button size="sm" variant="outline" disabled={sendingId === m.id + ":sms"} onClick={() => sendCode(m.id, ["sms"])} className="rounded-sm h-8 px-2" title="Code per SMS senden" data-testid={`club-send-sms-${m.id}`}>
                                                {sendingId === m.id + ":sms" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Smartphone className="w-3.5 h-3.5" />}
                                            </Button>
                                        )}
                                        {(m.email || m.phone) && (
                                            <Button size="sm" disabled={sendingId === m.id + ":all"} onClick={() => sendCode(m.id, ["email", "whatsapp", "sms"])} className="rounded-sm h-8 px-2 bg-[#FFCC00] text-[#111827] hover:bg-[#E6B800] font-bold" title="Alle verfügbaren Kanäle" data-testid={`club-send-all-${m.id}`}>
                                                {sendingId === m.id + ":all" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                            </Button>
                                        )}
                                        <Button size="sm" variant="outline" onClick={() => remove(m.id)} className="rounded-sm h-8 px-2 text-gray-500 hover:text-red-600" title="Entfernen" data-testid={`club-delete-${m.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
